import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Loader2, Store, Bike, User as UserIcon, Search } from "lucide-react";

const ROLE_ICON = { restaurant: <Store className="w-3.5 h-3.5" />, driver: <Bike className="w-3.5 h-3.5" />, client: <UserIcon className="w-3.5 h-3.5" /> };
const ROLE_LABEL = { restaurant: "Restaurateur", driver: "Livreur", client: "Client", admin: "Admin" };

export default function UsersAdminPanel() {
  const { t } = useTranslation();
  const [role, setRole] = useState("client");
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [query, setQuery] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      if (role === "restaurant") {
        const [u, r] = await Promise.all([
          apiClient.get(`/admin/users?role=restaurant`),
          apiClient.get(`/admin/restaurants`),
        ]);
        setUsers(u.data || []);
        setRestaurants(r.data || []);
      } else {
        const { data } = await apiClient.get(`/admin/users?role=${role}`);
        setUsers(data || []);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  }, [role]);

  useEffect(() => { refresh(); }, [refresh]);

  const removeUser = async (u) => {
    const label = u.name || u.email;
    if (!window.confirm(`Supprimer ${ROLE_LABEL[u.role]} "${label}" ?\n\nSi le compte a un historique de commandes, il sera anonymisé (soft delete). Sinon, il sera supprimé définitivement.`)) return;
    setBusy((b) => ({ ...b, [u.user_id]: true }));
    try {
      const { data } = await apiClient.delete(`/admin/users/${u.user_id}`);
      toast.success(data.mode === "soft" ? t("admin.softDeleted") : t("admin.hardDeleted"));
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy((b) => ({ ...b, [u.user_id]: false })); }
  };

  const removeRestaurant = async (r) => {
    if (!window.confirm(`Supprimer le restaurant "${r.name}" ?\n\nSi le resto a des commandes, il sera archivé (soft). Sinon supprimé définitivement.`)) return;
    setBusy((b) => ({ ...b, [r.restaurant_id]: true }));
    try {
      const { data } = await apiClient.delete(`/admin/restaurants/${r.restaurant_id}`);
      toast.success(data.mode === "soft" ? t("admin.restoArchived") : t("admin.restoDeleted"));
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy((b) => ({ ...b, [r.restaurant_id]: false })); }
  };

  const filteredUsers = useMemo(() => {
    if (!query) return users;
    const q = query.toLowerCase();
    return users.filter((u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || (u.phone || "").includes(q));
  }, [users, query]);

  return (
    <div className="space-y-4" data-testid="users-admin-panel">
      <div className="bg-white border border-sand rounded-2xl p-4 flex items-center gap-3 flex-wrap">
        <div className="flex gap-1">
          {["client", "restaurant", "driver"].map((k) => (
            <button
              key={k}
              onClick={() => setRole(k)}
              data-testid={`users-tab-${k}`}
              className={`text-xs rounded-full px-3 py-1 border inline-flex items-center gap-1.5 ${role === k ? "bg-ink text-white border-ink" : "bg-white text-ink border-sand"}`}
            >
              {ROLE_ICON[k]} {ROLE_LABEL[k]}s
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-mute" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("admin.searchUsers")}
            data-testid="users-search"
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-elevated text-sm outline-none focus:ring-2 focus:ring-brand-red/30"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-mute text-sm py-8 text-center">Chargement…</div>
      ) : role === "restaurant" ? (
        <RestaurantsBlock restaurants={restaurants} owners={users} busy={busy} onDeleteUser={removeUser} onDeleteRestaurant={removeRestaurant} />
      ) : (
        <UsersBlock users={filteredUsers} busy={busy} onDelete={removeUser} />
      )}
    </div>
  );
}

function UsersBlock({ users, busy, onDelete }) {
  const { t } = useTranslation();
  if (users.length === 0) return <div className="bg-white border border-sand rounded-2xl p-8 text-center text-mute text-sm">Aucun utilisateur.</div>;
  return (
    <div className="bg-white border border-sand rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-mute bg-elevated">
            <tr>
              <th className="text-left px-3 py-2">Nom</th>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">{t("admin.thPhone")}</th>
              <th className="text-left px-3 py-2">{t("admin.thCreated")}</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-t border-sand">
                <td className="px-3 py-2 font-semibold">{u.name || "—"}</td>
                <td className="px-3 py-2 text-mute text-xs">{u.email}</td>
                <td className="px-3 py-2 text-mute text-xs">{u.phone || "—"}</td>
                <td className="px-3 py-2 text-mute text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => onDelete(u)}
                    disabled={busy[u.user_id]}
                    data-testid={`delete-user-${u.user_id}`}
                    className="rounded-full border border-brand-red/40 text-brand-red hover:bg-brand-red hover:text-white transition-colors px-3 py-1 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
                  >
                    {busy[u.user_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RestaurantsBlock({ restaurants, owners, busy, onDeleteUser, onDeleteRestaurant }) {
  const { t } = useTranslation();
  const ownerMap = new Map(owners.map((o) => [o.user_id, o]));
  return (
    <div className="space-y-3">
      <UsersBlock users={owners} busy={busy} onDelete={onDeleteUser} />
      <div className="text-xs uppercase tracking-widest text-mute mt-4 mb-2">Restaurants ({restaurants.length})</div>
      {restaurants.length === 0 ? (
        <div className="bg-white border border-sand rounded-2xl p-6 text-center text-mute text-sm">Aucun restaurant.</div>
      ) : (
        <div className="bg-white border border-sand rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-mute bg-elevated">
                <tr>
                  <th className="text-left px-3 py-2">Nom</th>
                  <th className="text-left px-3 py-2">{t("admin.thOwner")}</th>
                  <th className="text-left px-3 py-2">Zone</th>
                  <th className="text-left px-3 py-2">Statut</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {restaurants.map((r) => {
                  const owner = ownerMap.get(r.owner_id);
                  return (
                    <tr key={r.restaurant_id} data-testid={`resto-row-${r.restaurant_id}`} className="border-t border-sand">
                      <td className="px-3 py-2 font-semibold">{r.name}</td>
                      <td className="px-3 py-2 text-mute text-xs">{owner?.name || owner?.email || "—"}</td>
                      <td className="px-3 py-2 text-mute text-xs">{r.zone || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${r.status === "active" ? "bg-brand-green/10 text-brand-green" : r.status === "deleted" ? "bg-brand-red/10 text-brand-red" : "bg-elevated text-mute"}`}>
                          {r.status || "inconnu"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => onDeleteRestaurant(r)}
                          disabled={busy[r.restaurant_id]}
                          data-testid={`delete-restaurant-${r.restaurant_id}`}
                          className="rounded-full border border-brand-red/40 text-brand-red hover:bg-brand-red hover:text-white transition-colors px-3 py-1 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
                        >
                          {busy[r.restaurant_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
